import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import {
  CONTRACT_STATUSES,
  CONTRACT_STATUS_LABELS,
  CONTRACT_TYPES,
  CONTRACT_TYPE_LABELS,
  formatDate,
  formatHours,
  type Paginated,
  type Permission,
  type ServiceAgreementTerms,
} from '@codepass/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import {
  Alert,
  Badge,
  Button,
  Card,
  Combobox,
  Input,
  MultiSelect,
  Progress,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import {
  Avatar,
  ContractStatusBadge,
  EmptyState,
  FilterField,
  FilterPanel,
  Money,
  PageHeader,
  Pagination,
  Percent,
  PermittedLink,
  RefreshButton,
} from '@/components/domain';
import { cn } from '@/lib/cn';
import { contractPath } from '@/lib/paths';
import type { ContractFilterOptions, ContractListItem } from '@/types';

/**
 * Ti raekker pr. side. En kontraktraekke er hoej - titel, kontraktnummer, kunde, en
 * fremdriftslinje og et beloeb under den - saa 50 blev en side man scrollede i stedet for at
 * laese. Serveren accepterer 1-200 (paginationSchema), saa tallet sendes med som det er.
 */
const LIMIT = 10;

/** PRD par. 5.1: filter-ændringer debouncer 300 ms, så tastning ikke sender et kald pr. tegn. */
const FILTER_DEBOUNCE_MS = 300;

/**
 * De kolonner serveren kan sortere på — dem der er rigtige kolonner i `contracts`. Faktureret,
 * Timer, PM og Alarmer beregnes i berigelsen og kan derfor ikke sorteres i databasen; deres
 * hoveder står uden sorteringsknap frem for at love noget ruten ikke kan holde.
 *
 * `end_date` står ikke her længere. Slutdato er væk fra siden i det hele: kolonnen,
 * sorteringen og de to datofelter i filterpanelet. Ruten kender den heller ikke længere — se
 * `sort`-enum i routes/contracts.ts, som falder tilbage på `created_at` frem for at svare 400
 * på et gammelt bogmærke med `?sort=end_date`.
 */
type SortColumn = 'title' | 'contract_type' | 'status' | 'contract_sum_excl_vat';

/** Ny kolonne starter faldende på beløb (størst først), ellers stigende (A–Å, tidligst). */
function defaultDirection(column: SortColumn): 'asc' | 'desc' {
  return column === 'contract_sum_excl_vat' ? 'desc' : 'asc';
}

/**
 * Kolonnen Type: CRM-feltet `Kontrakt type`, som det står i Dynamics.
 *
 * Ingen oversættelse og intet opslag i CONTRACT_TYPE_LABELS. `contract_type` er Codepass' egen
 * udledning af etiketten (`fixed_price`, `recurring`) og falder tilbage på `fixed_price`, når
 * feltet står tomt eller har en værdi Codepass ikke kender — så en kolonne bygget på den kunne
 * vise 'Fastpris' på en aftale, hvor CRM ikke siger noget. Em-streg er det rigtige svar dér.
 *
 * En kontrakt fra databasen bærer ikke feltet; den falder tilbage på Codepass' egen etiket, så
 * kolonnen ikke bliver tom i demo-tilstand.
 */
function contractTypeText(contract: ContractListItem): string {
  const label = contract.contract_type_label?.trim();
  if (label) return label;
  if (contract.contract_type_label === undefined)
    return CONTRACT_TYPE_LABELS[contract.contract_type];
  return '—';
}

/**
 * Valgene i typefilteret.
 *
 * Serveren læser dem af de aftaler der faktisk findes, så en tredje type i CRM kan vælges uden
 * en ændring her. Svarer den ikke med nogen — fixture-tilstand — falder filteret tilbage på
 * Codepass' egne typer, så panelet ikke står med en tom dropdown.
 */
function contractTypeOptions(
  options: ContractFilterOptions | undefined,
): Array<{ value: string; label: string }> {
  const fromCrm = options?.contract_types ?? [];
  if (fromCrm.length > 0) return fromCrm;
  return CONTRACT_TYPES.map((type) => ({ value: type, label: CONTRACT_TYPE_LABELS[type] }));
}

/**
 * Standardfilteret: /kontrakter åbner på de aftaler der er i gang — status 'Aktiv'.
 *
 * Listen er en arbejdsliste. Tabte og aftaler under forhandling er langt de fleste rækker i
 * Dynamics, og de er ikke det, en controller åbner siden for; uden et filter fra begyndelsen
 * skulle det samme klik sættes hver gang.
 *
 * **Standarden skrives til URL'en og ikke kun til kaldet.** Så viser filterpanelet 'Aktiv' som
 * valgt, og 'Nulstil filtre' kan fjerne den igen — et filter man ikke kan se, er et filter man
 * ikke kan slå fra. Den sættes én gang ved indlæsning: er der allerede et filter i adressen
 * (et delt link, et bookmark), står det uberørt.
 */
const DEFAULT_STATUS = 'active';

/**
 * De to lister: projektaftaler og serviceaftaler. Samme nøgler som `CONTRACT_KINDS` i API'et.
 *
 * To lister og ikke én med en ekstra kolonne, fordi de to slags aftale ikke har de samme tal. En
 * projektaftale har en kontraktsum, et timeloft og en fakturering, der måles i procent af summen.
 * En serviceaftale har en timepris, timer pr. opgave og en fakturering, der bare er et beløb —
 * der er ingen sum at måle den mod. I samme kolonne ville de to tal se sammenlignelige ud uden at
 * være det.
 */
type ContractKind = 'project' | 'service';

/** Hver fane er sin egen rettighed - 'Se kontrakter' og 'Se serviceaftaler'. */
const KIND_TABS: Array<{ id: ContractKind; label: string; permission: Permission }> = [
  { id: 'project', label: 'Projektaftaler', permission: 'contracts.view' },
  { id: 'service', label: 'Serviceaftaler', permission: 'service_agreements.view' },
];

/**
 * Listen i adressen, eller den foerste, brugeren maa se. Uden 'Se kontrakter' er en adresse uden
 * `kind` serviceaftalerne - ellers ville menupunktet aabne paa en liste, serveren svarer tom paa.
 */
function resolveKind(raw: string | null, allowed: readonly ContractKind[]): ContractKind {
  const requested: ContractKind = raw === 'service' ? 'service' : 'project';
  return allowed.includes(requested) ? requested : (allowed[0] ?? 'project');
}

/**
 * Filtre, der kun giver mening på projektaftalerne, og som derfor fjernes, når der skiftes til
 * serviceaftalerne. Et filter, man ikke kan se, er et filter man ikke kan slå fra — se
 * `DEFAULT_STATUS`.
 */
const PROJECT_ONLY_FILTERS = ['contract_type', 'min_billed_pct', 'is_amendment'];

/**
 * Bærer adressen et filter? Sortering alene tæller ikke — den filtrerer ingen rækker væk. Det gør
 * listen heller ikke: fanen er et valg af liste, ikke et filter, og 'Nulstil filtre' rører den ikke.
 */
function hasFilterParams(params: URLSearchParams): boolean {
  return [...params.keys()].some((key) => key !== 'sort' && key !== 'direction' && key !== 'kind');
}

/** URL'ens filtre, eller standarden når den er tom. Samme form begge steder, se `filterKey`. */
function withDefaultFilters(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.set('status', DEFAULT_STATUS);
  return next;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Filtervalgene til de to søgbare dropdowns. Skifter sjældent, så de må ligge længe i cachen. */
function useContractFilterOptions() {
  return useQuery({
    queryKey: ['contract-filters'],
    queryFn: () => api.get<ContractFilterOptions>('/contracts/filters'),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * F1.6 - kontraktliste (PRD par. 5.1).
 *
 * Filtrene ligger i URL'ens query params, så en filtreret visning kan deles og bookmarkes —
 * det er også hvad PRD kræver. Selve kaldet debounces 300 ms via en forsinket kopi af
 * søgestrengen: felterne styres af URL'en og reagerer med det samme, men netværkskaldet venter,
 * til tastningen falder til ro. Filterpanelet spejler /timer's mønstre (MultiSelect, Combobox).
 */
export function ContractsPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [offset, setOffset] = useState(0);
  const options = useContractFilterOptions();
  const tabs = KIND_TABS.filter((tab) => can(tab.permission));
  const allowedKinds = tabs.map((tab) => tab.id);

  const get = (key: string): string => params.get(key) ?? '';
  const list = (key: string): string[] => {
    const raw = get(key);
    return raw ? raw.split(',') : [];
  };

  /** Skriver ét filter til URL'en og nulstiller sidetælleren — en ny side ville være forældet. */
  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setOffset(0);
  };

  const setParamList = (key: string, values: string[]): void => setParam(key, values.join(','));

  /* Projektaftalerne er standarden: det er dem, der er flest af, og dem siden var før. */
  const kind = resolveKind(params.get('kind'), allowedKinds);
  const isService = kind === 'service';

  /**
   * Skifter liste. De filtre, den anden liste ikke har, tages af — ellers ville de filtrere
   * usynligt videre — og sidetælleren nulstilles, fordi side tre af én liste ikke er side tre af
   * den anden.
   */
  const setKind = (next: ContractKind): void => {
    const updated = new URLSearchParams(params);
    if (next === 'service') {
      updated.set('kind', 'service');
      for (const key of PROJECT_ONLY_FILTERS) updated.delete(key);
    } else {
      updated.delete('kind');
    }
    setParams(updated, { replace: true });
    setOffset(0);
  };

  const sort = (get('sort') || 'created_at') as SortColumn | 'created_at';
  const direction = (get('direction') || 'desc') as 'asc' | 'desc';

  const onSort = (column: SortColumn): void => {
    const next = new URLSearchParams(params);
    if (sort === column) {
      next.set('direction', direction === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sort', column);
      next.set('direction', defaultDirection(column));
    }
    setParams(next, { replace: true });
    setOffset(0);
  };

  /**
   * Skal standardfilteret stadig sættes? Afgjort af adressen ved første render og kun der.
   *
   * En tilstand og ikke en beregning pr. render: efter 'Nulstil filtre' er adressen tom igen,
   * og en beregning ville sætte 'Aktiv' tilbage med det samme, så filteret ikke kunne fjernes.
   */
  const [pendingDefaults, setPendingDefaults] = useState(() => !hasFilterParams(params));

  useEffect(() => {
    if (!pendingDefaults) return;
    setPendingDefaults(false);
    setParams(withDefaultFilters(params), { replace: true });
  }, [pendingDefaults, params, setParams]);

  /**
   * Kun filterdelen debounces. Sidetælleren holdes udenfor, så et klik i pagineringen henter
   * med det samme — en forsinkelse dér ville føles som en fejl.
   *
   * Standarden lægges på **her** og ikke først når effekten ovenfor har skrevet den til
   * adressen. Ellers ville første render hente hele listen ufiltreret, og skiftet af adressen
   * ét øjeblik senere ville hente den igen — to kald til Dataverse og BC for én sidevisning.
   * Nøglen bliver den samme streng begge gange, så det andet kald rammer cachen.
   */
  const filterKey = (pendingDefaults ? withDefaultFilters(params) : params).toString();
  const debouncedFilterKey = useDebounced(filterKey, FILTER_DEBOUNCE_MS);

  /* Som streng, saa den kan staa i afhaengighederne nedenfor uden at skifte ved hver render. */
  const allowedKey = allowedKinds.join(',');

  const query = useMemo(() => {
    const active = new URLSearchParams(debouncedFilterKey);
    const allowed = allowedKey.split(',').filter(Boolean) as ContractKind[];
    const value = (key: string): string | undefined => active.get(key) || undefined;
    return {
      /* Altid med, også for projektaftalerne: uden `kind` svarer serveren med begge lister. */
      kind: resolveKind(active.get('kind'), allowed),
      status: value('status'),
      contract_type: value('contract_type'),
      customer_id: value('customer_id'),
      responsible_pm_id: value('responsible_pm_id'),
      min_amount: value('min_amount'),
      max_amount: value('max_amount'),
      min_billed_pct: value('min_billed_pct'),
      has_alerts: value('has_alerts'),
      is_amendment: value('is_amendment'),
      search: value('search'),
      sort: active.get('sort') || 'created_at',
      direction: active.get('direction') || 'desc',
    };
  }, [debouncedFilterKey, allowedKey]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['contracts', debouncedFilterKey, offset],
    queryFn: () =>
      api.get<Paginated<ContractListItem>>('/contracts', { ...query, limit: LIMIT, offset }),
    placeholderData: (previous) => previous,
  });

  const minBilledPct = get('min_billed_pct');
  /* Standardfilteret tæller med: det står i adressen, og så skal det også kunne nulstilles. */
  const hasFilters = hasFilterParams(params);

  /* Listen bliver stående — se `hasFilterParams`. */
  const resetFilters = (): void => {
    setParams(new URLSearchParams(isService ? { kind: 'service' } : {}), { replace: true });
    setOffset(0);
  };

  return (
    <>
      <PageHeader
        title="Kontrakter"
        description="Synkroniseret fra GetAccept. Kontrakter oprettes ikke i Codepass."
        // Filtervalgene med: en ny aftale kan baere en kunde eller en PM, der ikke stod i
        // dropdownen foer - og de ligger 10 minutter i cachen (se useContractFilterOptions).
        actions={<RefreshButton prefixes={['contracts', 'contract-filters']} />}
      />

      {/* Samme fanebjælke som på /kontrakter/:id, så de to sider skifter visning på samme måde. */}
      <nav className="mb-4 flex flex-wrap gap-1 border-b border-line" role="tablist">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={kind === item.id}
            onClick={() => setKind(item.id)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm',
              kind === item.id
                ? 'border-brand font-medium text-brand-800'
                : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <div className="flex flex-col gap-4 lg:flex-row">
        <aside className="w-full shrink-0 lg:w-[280px]">
          <FilterPanel
            title={
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Filtre
              </span>
            }
            active={hasFilters}
            action={
              <Button size="sm" variant="ghost" disabled={!hasFilters} onClick={resetFilters}>
                Nulstil filtre
              </Button>
            }
          >
            <FilterField label="Søg i titel eller nummer">
              <Input
                value={get('search')}
                placeholder="Fx: Keymate eller ONC-1011"
                onChange={(event) => setParam('search', event.target.value)}
              />
            </FilterField>

            <FilterField label="Status">
              <MultiSelect
                values={list('status')}
                onChange={(values) => setParamList('status', values)}
                options={CONTRACT_STATUSES.map((status) => ({
                  value: status,
                  label: CONTRACT_STATUS_LABELS[status],
                }))}
                allLabel="Alle statusser"
              />
            </FilterField>

            {/* Serviceaftalerne har kun én type — det er det, der gør dem til en liste for sig. */}
            {isService ? null : (
              <FilterField label="Kontrakttype">
                <MultiSelect
                  values={list('contract_type')}
                  onChange={(values) => setParamList('contract_type', values)}
                  options={contractTypeOptions(options.data)}
                  allLabel="Alle typer"
                />
              </FilterField>
            )}

            <FilterField label="Kunde">
              <Combobox
                value={get('customer_id')}
                onChange={(value) => setParam('customer_id', value)}
                options={(options.data?.customers ?? []).map((row) => ({
                  value: row.id,
                  label: row.label,
                }))}
                allLabel="Alle kunder"
                searchPlaceholder="Søg kunde"
              />
            </FilterField>

            <FilterField label="Project Manager">
              <Combobox
                value={get('responsible_pm_id')}
                onChange={(value) => setParam('responsible_pm_id', value)}
                options={(options.data?.project_managers ?? []).map((row) => ({
                  value: row.id,
                  label: row.label,
                }))}
                allLabel="Alle PM'er"
                searchPlaceholder="Søg PM"
              />
            </FilterField>

            {/*
              Ikke på serviceaftalerne: de har ingen kontraktsum, så der er intet at måle
              faktureringen i procent af.
            */}
            {isService ? null : (
              <FilterField label={`Faktureret mindst ${minBilledPct || '0'} %`}>
                {/*
                  En enkelt min-skyder frem for et interval: det praktiske spørgsmål er "vis
                  kontrakter der er mindst X % fakturerede". 0 betyder ingen afgrænsning, så
                  skyderen fjernes fra URL'en der.
                */}
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={minBilledPct || '0'}
                  onChange={(event) =>
                    setParam('min_billed_pct', event.target.value === '0' ? '' : event.target.value)
                  }
                  className="w-full accent-brand"
                  aria-label="Faktureret mindst (procent)"
                />
              </FilterField>
            )}

            {/* På serviceaftalerne er beløbet timeprisen — det, kolonnen Kontraktsum viser dér. */}
            <FilterField
              label={isService ? 'Timepris (kr, ekskl. moms)' : 'Beløbsinterval (kr, ekskl. moms)'}
            >
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  placeholder="Fra"
                  value={get('min_amount')}
                  onChange={(event) => setParam('min_amount', event.target.value)}
                />
                <span className="text-xs text-ink-muted">–</span>
                <Input
                  type="number"
                  min={0}
                  placeholder="Til"
                  value={get('max_amount')}
                  onChange={(event) => setParam('max_amount', event.target.value)}
                />
              </div>
            </FilterField>

            <label className="flex items-center gap-2 rounded-md border border-line p-2 text-xs">
              <input
                type="checkbox"
                checked={get('has_alerts') === 'true'}
                onChange={(event) => setParam('has_alerts', event.target.checked ? 'true' : '')}
              />
              <span className="font-medium">Har åbne alarmer</span>
            </label>

            {/* En serviceaftale har ingen tillægsaftaler. */}
            {isService ? null : (
              <FilterField label="Aftaletype">
                {/* Ekstra ud over PRD, men billig: adskiller hovedaftaler fra tillæg. */}
                <select
                  value={get('is_amendment')}
                  onChange={(event) => setParam('is_amendment', event.target.value)}
                  className="h-9 w-full rounded-md border border-line bg-surface px-2 text-sm"
                >
                  <option value="">Alle</option>
                  <option value="false">Kun hovedaftaler</option>
                  <option value="true">Kun tillægsaftaler</option>
                </select>
              </FilterField>
            )}
          </FilterPanel>
        </aside>

        <div className="min-w-0 flex-1">
          <Card>
            {isLoading ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2, 3, 4].map((index) => (
                  <Skeleton key={index} className="h-10" />
                ))}
              </div>
            ) : error ? (
              <Alert tone="error" className="m-4">
                Kunne ikke hente kontrakter.
              </Alert>
            ) : (data?.data.length ?? 0) === 0 ? (
              <EmptyState
                title={
                  hasFilters
                    ? isService
                      ? 'Ingen serviceaftaler matcher filtrene'
                      : 'Ingen kontrakter matcher filtrene'
                    : isService
                      ? 'Ingen serviceaftaler endnu'
                      : 'Ingen kontrakter endnu'
                }
                description={
                  hasFilters
                    ? undefined
                    : 'Kontrakter synkroniseres automatisk fra GetAccept hver 15. minut.'
                }
                action={
                  hasFilters ? (
                    <Button size="sm" variant="secondary" onClick={resetFilters}>
                      Nulstil filtre
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <>
                <Table stickyHeader>
                  <thead>
                    <tr>
                      <SortHeader
                        label="Titel"
                        column="title"
                        sort={sort}
                        direction={direction}
                        onSort={onSort}
                      />
                      {/* Kun på projektaftalerne: serviceaftalerne har alle den samme type. */}
                      {isService ? null : (
                        <SortHeader
                          label="Type"
                          column="contract_type"
                          sort={sort}
                          direction={direction}
                          onSort={onSort}
                          className="w-[120px]"
                        />
                      )}
                      <SortHeader
                        label="Status"
                        column="status"
                        sort={sort}
                        direction={direction}
                        onSort={onSort}
                        className="w-[130px]"
                      />
                      <SortHeader
                        label="Kontraktsum"
                        column="contract_sum_excl_vat"
                        sort={sort}
                        direction={direction}
                        onSort={onSort}
                        className="w-[140px] text-right"
                      />
                      <Th className={cn('w-[180px]', isService && 'text-right')}>Faktureret</Th>
                      <Th
                        className="w-[110px] text-right"
                        title={
                          isService
                            ? 'Timer pr. opgave: det, en opgave må bruge, før kunden skal spørges'
                            : undefined
                        }
                      >
                        Timer
                      </Th>
                      <Th className="w-[150px]">PM</Th>
                      {/*
                        Signeringsdatoen. Uden sorteringsknap: `signed_date` er en rigtig
                        kolonne og *kunne* sorteres, men listen er en arbejdsliste sorteret på
                        nyeste først, og datoen er her som oplysning om den enkelte aftale.
                        Skal den kunne sorteres, hører den i `SortColumn` og i `sort`-enum'en i
                        routes/contracts.ts — begge steder, ellers afviser serveren nøglen.
                      */}
                      <Th className="w-[120px]">Signeret</Th>
                      <Th className="w-[80px] text-center">Alarmer</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.data.map((contract) => (
                      <Tr key={contract.id}>
                        <Td>
                          <Link to={contractPath(contract)} className="font-medium hover:underline">
                            {contract.title}
                          </Link>
                          <span className="mt-0.5 flex items-center gap-2 text-xs text-ink-muted">
                            {contract.contract_no ? (
                              <span className="tabular text-ink-muted">{contract.contract_no}</span>
                            ) : null}
                            {contract.customer?.name ?? '—'}
                            {contract.parent_contract_id ? (
                              <Badge tone="brand">Tillæg</Badge>
                            ) : null}
                          </span>
                        </Td>
                        {isService ? null : (
                          <Td>
                            {/* CRM-feltet 'Kontrakt type', råt og uoversat — se contractTypeText. */}
                            <Badge tone="neutral">{contractTypeText(contract)}</Badge>
                          </Td>
                        )}
                        <Td>
                          <ContractStatusBadge status={contract.status} />
                        </Td>
                        {contract.service_agreement ? (
                          <ServiceAgreementCells
                            terms={contract.service_agreement}
                            contract={contract}
                          />
                        ) : (
                          <>
                            <Td className="text-right">
                              <Money value={contract.contract_sum_excl_vat} decimals={0} />
                            </Td>
                            <Td>
                              <Progress value={contract.billed_pct} />
                              <span className="mt-1 block text-xs text-ink-muted">
                                <Money value={contract.billed_excl_vat} decimals={0} />
                                {' · '}
                                <Percent value={contract.billed_pct} />
                              </span>
                            </Td>
                            <Td className="text-right tabular">
                              {contract.hours_cap
                                ? `${formatHours(contract.used_hours)} / ${contract.hours_cap}`
                                : '—'}
                            </Td>
                          </>
                        )}
                        <Td className="text-xs">
                          {contract.pm ? (
                            <span className="flex items-center gap-2">
                              <Avatar name={contract.pm.name} url={contract.pm.avatar_url} />
                              <span className="truncate">{contract.pm.name}</span>
                            </span>
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </Td>
                        {/*
                          Em-streg og ikke 'Nej': kolonnen er en dato, og en aftale uden den
                          kan både være usigneret og signeret uden at datoen er sat. Svaret på
                          *om* den er signeret står på /kontrakter/:id, hvor der er plads til
                          begge dele — se `signedValue`.
                        */}
                        <Td className="text-xs tabular">{formatDate(contract.signed_date)}</Td>
                        <Td className="text-center">
                          {contract.open_alert_count > 0 ? (
                            /*
                             * Tallet er alles alarmer paa aftalen, saa listen skal ogsaa vaere
                             * det - and only 'Se alle alarmer' shows everyone's. Without it the
                             * list would hold fewer than the badge says, so it is just the number.
                             */
                            <PermittedLink
                              to={`/alarmer?contract_id=${contract.id}&recipient=all`}
                              allowed={can('alerts.view_all')}
                            >
                              <Badge tone="danger">{contract.open_alert_count}</Badge>
                            </PermittedLink>
                          ) : (
                            <span className="text-xs text-ink-muted">—</span>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
                <Pagination
                  total={data?.total ?? 0}
                  limit={LIMIT}
                  offset={offset}
                  onChange={setOffset}
                />
              </>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

/**
 * Kontraktsum, Faktureret og Timer på en serviceaftale.
 *
 * Samme tre kolonner som på en projektaftale, men ikke de samme tal:
 *
 *   Kontraktsum  timeprisen — 'Løbende serviceaftale' i CRM — det kunden betaler pr. time
 *   Faktureret   beløbet alene. Ingen bjælke og ingen procent: der er ingen sum at måle mod
 *   Timer        timer pr. opgave — det, en opgave må bruge, før kunden skal spørges
 *
 * Em-streg, når CRM ikke har vilkåret. Et nul ville se ud som et aftalt vilkår.
 */
function ServiceAgreementCells({
  terms,
  contract,
}: {
  terms: ServiceAgreementTerms;
  contract: ContractListItem;
}): ReactNode {
  return (
    <>
      <Td className="text-right">
        {terms.hourly_rate_excl_vat === null ? (
          <span className="text-ink-muted">—</span>
        ) : (
          <>
            <Money value={terms.hourly_rate_excl_vat} decimals={0} />
            <span className="block text-xs text-ink-muted">pr. time</span>
          </>
        )}
      </Td>
      <Td className="text-right">
        <Money value={contract.billed_excl_vat} decimals={0} />
      </Td>
      <Td className="text-right tabular">
        {terms.hours_per_task === null ? (
          <span className="text-ink-muted">—</span>
        ) : (
          <>
            {formatHours(terms.hours_per_task)}
            <span className="block text-xs text-ink-muted">pr. opgave</span>
          </>
        )}
      </Td>
    </>
  );
}

/**
 * Et sorterbart tabelhoved. Viser en pil på den aktive kolonne og en dæmpet dobbeltpil på de
 * øvrige, så det er tydeligt hvad man kan sortere efter — og hvad der er valgt.
 */
function SortHeader({
  label,
  column,
  sort,
  direction,
  onSort,
  className,
}: {
  label: string;
  column: SortColumn;
  sort: string;
  direction: 'asc' | 'desc';
  onSort: (column: SortColumn) => void;
  className?: string;
}): ReactNode {
  const active = sort === column;
  const Icon = active ? (direction === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;

  return (
    <Th className={className}>
      <button
        type="button"
        onClick={() => onSort(column)}
        aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        className="inline-flex items-center gap-1 font-semibold text-ink-muted hover:text-ink"
      >
        {label}
        <Icon
          className={cn('h-3.5 w-3.5', active ? 'text-brand' : 'text-ink-muted/40')}
          aria-hidden
        />
      </button>
    </Th>
  );
}
